<template>
  <page-loader>
    <v-container>
      <v-card outlined>
        <v-card-text>
          <v-row>
            <v-col class="d-flex align-center">
              <text-input
                v-model="entry.partner_portal"
                :disabled="!isPermittedTo('create')"
                class="mr-4"
                label="Ad Account"
              />
              <autocomplete-input
                v-model="entry.currency"
                :disabled="!isPermittedTo('create')"
                :items="currencies"
                class="mr-4"
                label="Currency"
              />
            </v-col>
          </v-row>
          <v-row v-for="(event, index) in entry.events" :key="index">
            <v-col class="d-flex align-center">
              <date-picker
                v-model="event.date"
                :disabled="!canAddRows"
                :error="event.date == undefined"
                format="DDMMYYYY"
                label="Date"
                hide-details
              />
              <autocomplete-input
                v-model="event.revenue_type"
                :disabled="!canAddRows"
                :error="event.revenue_type == undefined"
                :items="revenueTypes"
                :item-disabled="isRevenueItemDisabled(event.event_type)"
                class="ml-4 mb-1"
                label="Revenue Type"
                auto-select-first
                cache-items
                hide-details
              />
              <autocomplete-input
                v-model="event.offer_type"
                :disabled="!canAddRows"
                :items="offerTypes"
                class="ml-4 mb-1"
                label="Offer Type"
                hide-details
              />
              <autocomplete-input
                v-model="event.country"
                :disabled="!canAddRows"
                :items="countries"
                class="ml-4 mb-1"
                label="Country"
                hide-details
              />
              <autocomplete-input
                v-model="event.website"
                :disabled="!canAddRows"
                :items="sites"
                class="my-1 ml-4"
                label="Website"
                hide-details
              />
              <text-input
                v-model="event.raw_amount"
                :disabled="!canAddRows"
                :error="event.raw_amount == undefined"
                class="my-1 ml-4"
                label="Amount"
                type="number"
                hide-details
              />
            </v-col>
            <v-col class="d-flex align-center flex-grow-0 pl-0">
              <v-btn
                :disabled="!canAddRows || entry.events.length === 1"
                color="error"
                depressed
                small
                @click="onClickDeleteRow(index)"
              >
                <v-icon small>close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex justify-end">
              <v-btn :disabled="!canAddRows" class="ml-4" color="primary" depressed @click="onClickAddRow">
                <v-icon left>add</v-icon>
                Add new row
              </v-btn>
              <v-btn :disabled="!canAddRows" class="ml-4" color="secondary" depressed @click="onClickCloneRow">
                <v-icon left>library_add</v-icon>
                Clone last row
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row>
        <v-col class="d-flex justify-end">
          <v-btn :disabled="!canSave" :loading="isLoading" class="ml-4" color="info" depressed @click="onClickSave">
            <v-icon left>save</v-icon>
            Save events
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-title>
              Pending Events - £{{ totalValueInTable }}
              <v-spacer></v-spacer>
              <text-input v-model="search" label="Search" append-icon="search" />
            </v-card-title>
            <v-card-text>
              <v-data-table ref="pending" :headers="headers" :search="search" :items="eventLog" dense>
                <template #item.date="{ value }">
                  {{ formatDate(value, { separator: '-' }) }}
                </template>
                <template #item.click_id="{ value }">
                  <valid-click-id :value="value" />
                </template>
                <template #item.amount="{ value }">
                  {{ (value / 100).toFixed(2) }}
                </template>
                <template #item.edit="{ value }">
                  {{ (value / 100).toFixed(2) }}
                </template>
                <template #item.actions="{ item }">
                  <v-icon :disabled="!isPermittedTo('update')" class="mr-2" small @click="onEditItem(item.uuid)">
                    edit
                  </v-icon>
                  <v-icon :disabled="!isPermittedTo('delete')" small @click="onDeleteItem(item.uuid)">
                    delete
                  </v-icon>
                </template>
              </v-data-table>
              <v-row>
                <v-col class="d-flex justify-end">
                  <v-btn
                    :disabled="!isPermittedTo('create') || !eventLog.length"
                    :loading="isLoading"
                    class="ml-4"
                    color="info"
                    depressed
                    @click="onClickUpload"
                  >
                    <v-icon left>cloud_upload</v-icon>
                    Upload data
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
    <edit-event-modal
      :event="editingEvent"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showEditModal"
      @modal-confirm="onEditConfirm"
      @modal-cancel="onEditCancel"
    >
      <v-row>
        <v-col sm="4">
          <text-input v-model="editingEvent.partner_portal" label="Ad Account" />
        </v-col>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.currency" :items="currencies" label="Currency" />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="4">
          <date-picker
            v-model="editingEvent.date"
            :error="editingEvent.date == undefined"
            format="DDMMYYYY"
            label="Date"
            hide-details
          />
        </v-col>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.revenue_type"
            :error="editingEvent.revenue_type == undefined"
            :items="revenueTypes"
            :item-disabled="isRevenueItemDisabled(editingEvent.event_type)"
            class="mb-1"
            label="Revenue Type"
            auto-select-first
            cache-items
            hide-details
          />
        </v-col>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.offer_type"
            :items="offerTypes"
            class="mb-1"
            label="Offer Type"
            hide-details
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.country"
            :items="countries"
            class="mb-1"
            label="Country"
            hide-details
          />
        </v-col>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.website" :items="sites" class="my-1" label="Website" hide-details />
        </v-col>
        <v-col sm="4">
          <text-input
            v-model="editingEvent.raw_amount"
            :error="editingEvent.raw_amount == undefined"
            class="my-1"
            label="Amount"
            type="number"
            hide-details
          />
        </v-col>
      </v-row>
    </edit-event-modal>
  </page-loader>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { mapGetters, mapMutations } from 'vuex'

import AutocompleteInput from '@/components/forms/autocomplete-input'
import DatePicker from '@/components/forms/date-picker'
import EditEventModal from '@/views/data/components/edit-event-modal'
import PageLoader from '@/components/page-loader'
import TextInput from '@/components/forms/text-input'
import ValidClickId from '@/components/valid-click-id'

import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'

import integrations from '@/services/integrations'

import { formatDate } from '@/utils/date'

const entryModel = ({
  date = formatDate(new Date(), { format: 'YYYYMMDD', separator: '-' }),
  offer = null,
  country = null,
  website = null,
  click_id = null,
  raw_amount = null,
} = {}) => ({
  date,
  event_type: 'manualCost',
  revenue_type: 'ADSPEND',
  offer,
  country,
  website,
  click_id,
  raw_amount,
})

export default {
  name: 'ExpenditureDataEntry',
  components: {
    AutocompleteInput,
    DatePicker,
    EditEventModal,
    PageLoader,
    TextInput,
    ValidClickId,
  },
  mixins: [DataModification, PermissionsHandler],
  data: () => ({
    isLoading: false,
    search: '',
    totalValueInTable: 0,
    headers: [
      { text: 'Ad Account', value: 'partner_portal' },
      { text: 'Date', value: 'date', width: 110 },
      { text: 'Expenditure Type', value: 'revenue_type' },
      { text: 'Offer Type', value: 'offer_type' },
      { text: 'Country', value: 'country', sortable: false },
      { text: 'Amount (£)', align: 'end', value: 'amount' },
      { text: 'Actions', align: 'end', value: 'actions', sortable: false },
    ],
    eventLog: [],
    entry: {
      partner_portal: null,
      currency: 'USD',
      events: [entryModel()],
    },
    revenueTypes: [{ value: 'ADSPEND', key: 'Ad-spend', event_types: ['manualCost'] }],
    requiredFields: ['date', 'revenue_type', 'raw_amount'],
    showEditModal: false,
    editingEvent: {},
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    canSave() {
      return (
        this.canAddRows &&
        this.entry.events.reduce((canSave, event) => {
          const requiredDefined = Object.entries(event).every(
            ([key, value]) => !this.requiredFields.includes(key) || value != undefined
          )
          if (!requiredDefined) canSave = false
          return canSave
        }, true)
      )
    },
    countries() {
      return this.getItemsForOption('countries')
    },
    currencies() {
      return this.getItemsForOption('mainCurrencies')
    },
    offerTypes() {
      return this.getItemsForOption('offerTypes')
    },
    sites() {
      return this.getItemsForOption('sites')
    },
    canAddRows() {
      return !!this.entry.partner_portal && this.isPermittedTo('create')
    },
  },
  watch: {
    async eventLog() {
      await this.$nextTick()
      this.updateSumTotal()
    },
    async search() {
      await this.$nextTick()
      this.updateSumTotal()
    },
  },
  async created() {
    this.setBeforeUnloadListener(this.onBeforeUnload)
    await this.getEventLog()
    this.totalValueInTable = this.updateSumTotal()
    this.setPageLoading(false)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.entry.partner_portal) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    formatDate(str, opts) {
      return formatDate(str, opts)
    },
    updateSumTotal() {
      const filteredItems =
        this.$refs &&
        this.$refs.pending &&
        this.$refs.pending.$children &&
        this.$refs.pending.$children[0].filteredItems

      const toSum = filteredItems || this.eventLog
      const sum = toSum.reduce((total, { amount }) => total + amount, 0)

      this.totalValueInTable = sum ? (sum / 100).toFixed(2) : 0
    },
    filteredRevenueTypes(event_type) {
      return this.revenueTypes.filter(({ event_types }) => event_types.includes(event_type))
    },
    isRevenueItemDisabled(event_type) {
      return ({ event_types }) => !event_types.includes(event_type)
    },
    async getEventLog() {
      this.isLoading = true

      try {
        const response = await integrations.getEventLog({
          expense: true,
          pending: true,
        })
        this.$set(this, 'eventLog', response.events)
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to fetch event logs',
        })
      }

      this.isLoading = false
    },
    onEventTypeChange(index) {
      this.entry.events[index].revenue_type = null
    },
    onEditItem(uuid) {
      this.editingEvent = this.eventLog.find(event => event.uuid === uuid)
      this.showEditModal = true
    },
    async onDeleteItem(uuid) {
      this.isLoading = true
      await integrations.deleteEvent(uuid)
      await this.getEventLog()
      this.isLoading = false
    },
    async onClickUpload() {
      this.isLoading = true

      try {
        await integrations.sendToEventCollector({ expense: true, source: 'manual' })
        await this.getEventLog()
        this.$notify({
          type: 'success',
          text: 'Events uploaded',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to upload events',
        })
      }

      this.isLoading = false
    },
    onClickAddRow() {
      this.entry.events.push(entryModel())
    },
    onClickCloneRow() {
      const lastItem = cloneDeep(this.entry.events[this.entry.events.length - 1])
      this.entry.events.push(lastItem)
    },
    onClickDeleteRow(index) {
      const updatedEntries = this.entry.events.slice(0, index).concat(this.entry.events.slice(index + 1))
      this.$set(this.entry, 'events', updatedEntries)
    },
    async onClickSave() {
      this.isLoading = false

      try {
        await integrations.saveManualEvents(this.entry)
        await this.getEventLog()
        this.entry = {
          partner_portal: null,
          currency: 'USD',
          events: [entryModel()],
        }
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save events',
        })
      }

      this.isLoading = false
    },
    onBeforeUnload(event) {
      if (!this.entry.partner_portal) return next()
      this.confirmUnload(event)
    },
    async onEditConfirm() {
      this.isLoading = true

      try {
        await integrations.editEvent(this.editingEvent.uuid, this.editingEvent)
        await this.getEventLog()
        this.showEditModal = false
        this.editingEvent = {}
        this.$notify({
          type: 'success',
          text: 'Event Updated',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save event',
        })
      }

      this.isLoading = false
    },
    onEditCancel() {
      this.showEditModal = false
      this.editingEvent = {}
    },
  },
}
</script>
